import React from 'react';
import { useDebounce } from '@/hooks/use-debounce';
import { http } from '@/lib/http';
import { SearchResult } from '@/stores/search';
import { levenshtein } from '@/lib/utils';

export const useInlineSearch = (query: string, minLength: number = 3, debounceMs: number = 300) => {
  const [results, setResults] = React.useState<SearchResult[]>([]);
  const [loading, setLoading] = React.useState(false);
  const debouncedQuery = useDebounce(query, debounceMs);

  React.useEffect(() => {
    if (!debouncedQuery.trim() || debouncedQuery.length < minLength) {
      setResults([]);
      setLoading(false);
      return;
    }

    const search = async () => {
      setLoading(true);
      try {
        const codeParams = new URLSearchParams({ q: debouncedQuery, type: 'code' });

        const codeRes = await http<{ results: SearchResult[] }>(`/api/search?${codeParams.toString()}`);


        const uniqueResultsMap = new Map<string, SearchResult>();
        codeRes.results.forEach(result => {
          if (!result.path) return;
          const key = result.type === 'symbol' ? `${result.path}:${result.name}` : result.path;
          if (!uniqueResultsMap.has(key)) {
            uniqueResultsMap.set(key, result);
          }
        });
        const uniqueResults = Array.from(uniqueResultsMap.values());
        const lowerCaseQuery = debouncedQuery.toLowerCase();

        const extensionMatch = lowerCaseQuery.match(/\.([a-z0-9]+)$/);

        uniqueResults.sort((a, b) => {
          const aIsFile = a.type !== 'symbol';
          const bIsFile = b.type !== 'symbol';

          const aFilename = a.path!.split('/').pop()!.toLowerCase();
          const bFilename = b.path!.split('/').pop()!.toLowerCase();

          // Extension filtering and sorting
          if (extensionMatch) {
            const ext = extensionMatch[0];
            const aHasExt = aIsFile && aFilename.endsWith(ext);
            const bHasExt = bIsFile && bFilename.endsWith(ext);

            if (aHasExt && !bHasExt) return -1;
            if (!aHasExt && bHasExt) return 1;
            if (aHasExt && bHasExt) {
                // If both match extension, prioritize shorter paths
                if (a.path!.length !== b.path!.length) {
                    return a.path!.length - b.path!.length;
                }
            }
          }
          
          // Type: Files before symbols
          if (aIsFile && !bIsFile) return -1;
          if (!aIsFile && bIsFile) return 1;
          
          const aNameToCompare = aIsFile ? aFilename : (a.name || '').toLowerCase();
          const bNameToCompare = bIsFile ? bFilename : (b.name || '').toLowerCase();
          
          // Levenshtein distance
          const distA = levenshtein(lowerCaseQuery, aNameToCompare);
          const distB = levenshtein(lowerCaseQuery, bNameToCompare);
          if (distA !== distB) {
            return distA - distB;
          }

          // Path length as tie-breaker
          if (a.path!.length !== b.path!.length) {
              return a.path!.length - b.path!.length;
          }

          // Final tie-breaker: alphabetical
          return aNameToCompare.localeCompare(bNameToCompare);
        });

        setResults(uniqueResults);
      } catch (error) {
        console.error("Inline search failed", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    search();
  }, [debouncedQuery, minLength]);

  return { results, loading, debouncedQuery };
};